import { c as _c } from "react/compiler-runtime";
/**
 * Text Selection Hook
 *
 * Detects when user selects text within a specific container and provides
 * the selected text along with position information for displaying a popup.
 */

import { useState, useEffect, useCallback, RefObject } from "react";
type SelectionInfo = {
  text: string;
  x: number;
  y: number;
};
export function useTextSelection(containerRef) {
  const $ = _c(9);
  const [selection, setSelection] = useState(null);
  let t0;
  if ($[0] !== containerRef) {
    t0 = () => {
      const selectedText = window.getSelection()?.toString().trim();
      if (!selectedText || !containerRef.current) {
        setSelection(null);
        return;
      }
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) {
        setSelection(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const container = containerRef.current;
      if (!container.contains(range.commonAncestorContainer)) {
        setSelection(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      let x = rect.left + rect.width / 2;
      let y = rect.top - 10;
      const minY = containerRect.top + 50;
      const maxY = containerRect.bottom - 100;
      if (y < minY) {
        y = minY;
      } else {
        if (y > maxY) {
          y = maxY;
        }
      }
      const maxX = window.innerWidth - 100;
      if (x < 100) {
        x = 100;
      } else {
        if (x > maxX) {
          x = maxX;
        }
      }
      setSelection({
        text: selectedText,
        x,
        y
      });
    };
    $[0] = containerRef;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleSelectionChange = t0;
  let t1;
  let t2;
  if ($[2] !== containerRef || $[3] !== handleSelectionChange) {
    t1 = () => {
      document.addEventListener("selectionchange", handleSelectionChange);
      document.addEventListener("mouseup", handleSelectionChange);
      const handleScroll = () => {
        handleSelectionChange();
      };
      const container_0 = containerRef.current;
      if (container_0) {
        container_0.addEventListener("scroll", handleScroll);
      }
      return () => {
        document.removeEventListener("selectionchange", handleSelectionChange);
        document.removeEventListener("mouseup", handleSelectionChange);
        if (container_0) {
          container_0.removeEventListener("scroll", handleScroll);
        }
      };
    };
    t2 = [handleSelectionChange, containerRef];
    $[2] = containerRef;
    $[3] = handleSelectionChange;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setSelection(null);
      window.getSelection()?.removeAllRanges();
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const clearSelection = t3;
  let t4;
  if ($[7] !== selection) {
    t4 = {
      selection,
      clearSelection
    };
    $[7] = selection;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}